import { useIntl } from 'react-intl';
import {
  StockTransfer,
  StockTransferStatusEnum,
} from '../models/StockTransfer';

interface StockTransferListProps {
  items: StockTransfer[];
  loading: boolean;
  currentUserId?: number;
  onReceive: (id: number) => void;
  onDelete: (id: number) => void;
}

const getStatusBadgeClass = (status: StockTransferStatusEnum): string => {
  switch (status) {
    case StockTransferStatusEnum.PENDING:
      return 'badge-light-warning';
    case StockTransferStatusEnum.RECEIVED:
      return 'badge-light-success';
    case StockTransferStatusEnum.CANCELLED:
      return 'badge-light-danger';
    default:
      return 'badge-light';
  }
};

export function StockTransferList({
  items,
  loading,
  currentUserId,
  onReceive,
  onDelete,
}: StockTransferListProps) {
  const intl = useIntl();

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-10">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">
            {intl.formatMessage({ id: 'common.loading' })}
          </span>
        </div>
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <div className="py-10 text-muted">
        {intl.formatMessage({ id: 'stockTransfers.noResults' })}
      </div>
    );
  }

  return (
    <div className="table-responsive">
      <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
        <thead>
          <tr className="fw-bold text-muted">
            <th>{intl.formatMessage({ id: 'stockTransfers.code' })}</th>
            <th>{intl.formatMessage({ id: 'stockTransfers.warehouse' })}</th>
            <th>{intl.formatMessage({ id: 'stockTransfers.status' })}</th>
            <th>{intl.formatMessage({ id: 'stockTransfers.shipmentDate' })}</th>
            <th>
              {intl.formatMessage({ id: 'stockTransfers.expectedArrival' })}
            </th>
            <th>{intl.formatMessage({ id: 'stockTransfers.receivedDate' })}</th>
            <th>{intl.formatMessage({ id: 'stockTransfers.createdBy' })}</th>
            <th>{intl.formatMessage({ id: 'stockTransfers.lines' })}</th>
            <th>{intl.formatMessage({ id: 'stockTransfers.actions' })}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id}>
              <td>
                <span className="text-dark fw-bold">{item.code}</span>
              </td>
              <td>{item.warehouse_name || '-'}</td>
              <td>
                <span className={`badge ${getStatusBadgeClass(item.status)}`}>
                  {intl.formatMessage({
                    id: `stockTransfers.status.${item.status}`,
                  })}
                </span>
              </td>
              <td>
                {item.shipment_date
                  ? new Date(item.shipment_date).toLocaleDateString()
                  : '-'}
              </td>
              <td>
                {item.expected_arrival_date
                  ? new Date(item.expected_arrival_date).toLocaleDateString()
                  : '-'}
              </td>
              <td>
                {item.received_date
                  ? new Date(item.received_date).toLocaleDateString()
                  : '-'}
              </td>
              <td>{item.created_by_name || '-'}</td>
              <td>
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {item.lines_summary && item.lines_summary.length > 0 ? (
                    <ul
                      className="list-unstyled mb-0"
                      style={{ fontSize: '0.9rem' }}
                    >
                      {item.lines_summary.map((line, idx) => (
                        <li key={idx} className="mb-1">
                          • {line}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </div>
              </td>
              <td className="d-flex gap-2">
                {item.status === StockTransferStatusEnum.PENDING && (
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={() => onReceive(item.id)}
                  >
                    <i className="fa fa-check me-2" />
                    {intl.formatMessage({ id: 'stockTransfers.received' })}
                  </button>
                )}
                {item.status === StockTransferStatusEnum.PENDING &&
                  currentUserId != null &&
                  item.created_by === currentUserId && (
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={() => onDelete(item.id)}
                    >
                      <i className="fa fa-trash me-2" />
                      {intl.formatMessage({ id: 'stockTransfers.delete' })}
                    </button>
                  )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
